import React, { useEffect, useRef, useState } from 'react'
import {
	ChevronDownIcon,
	CheckCircleIcon,
	ExclamationCircleIcon,
	ArrowPathIcon,
	LinkIcon,
} from '@heroicons/react/24/outline'
import { useLoadboardTheme } from './loadboardTheme'
import { Label, makeInputBase } from './FilterFields'
import { SOURCE_LOGOS } from './sourceLogos'
import { callExtension, detectExtension, STORE_URL } from '../../utils/extensionBridge'
import api from '../../utils/apiClient'
import { EXTENSION_CONFIG } from '../../config/extensionConfig'

const { TIMEOUTS } = EXTENSION_CONFIG

const SOURCES = [
	{ key: 'TQL', label: 'TQL' },
	{ key: 'JBHUNT', label: 'JB Hunt' },
	// { key: 'RXO', label: 'RXO' },
	{ key: 'LANDSTAR', label: 'Landstar' },
]

function SourceDropdown({ value, onChange }) {
	const { theme, tokens } = useLoadboardTheme()
	const isLight = theme === 'light'
	const inputBase = makeInputBase(tokens)
	const activeItemCls = isLight
		? 'bg-sky-50 text-sky-700'
		: 'bg-sky-500/15 text-sky-200'
	const [isOpen, setIsOpen] = useState(false)
	const [connecting, setConnecting] = useState(null)
	const [connected, setConnected] = useState({})
	const [errors, setErrors] = useState({})
	const wrapRef = useRef(null)

	// Seed badge state from backend on mount. Bulk status endpoint returns
	// { TQL: true, JBHUNT: false, ... } based on which provider sessions
	// the user has stored.
	useEffect(() => {
		let cancelled = false
		api
			.get('/api/provider-sessions')
			.then(res => {
				if (cancelled || !res.data?.data) return
				setConnected(res.data.data)
			})
			.catch(() => {})
		return () => {
			cancelled = true
		}
	}, [])

	// Click-outside to close.
	useEffect(() => {
		if (!isOpen) return
		const onClick = e => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', onClick)
		return () => document.removeEventListener('mousedown', onClick)
	}, [isOpen])

	const messageForError = err => {
		if (err?.isNotInstalled) {
			return {
				message: 'LoadLine extension is not installed.',
				action: 'install',
			}
		}
		if (err?.isTimeout) return { message: 'Timed out — try again' }
		return { message: err?.message || 'Something went wrong' }
	}

	const gateOnExtension = async key => {
		const probe = await detectExtension()
		if (!probe.compatible) {
			setErrors(prev => ({
				...prev,
				[key]: { message: probe.message, action: probe.action },
			}))
			return false
		}
		return true
	}

	const handleConnect = async key => {
		setErrors(prev => ({ ...prev, [key]: null }))
		setConnecting(key)
		try {
			if (!(await gateOnExtension(key))) return
			const result = await callExtension(
				`${key}_CONNECT_START`,
				{},
				TIMEOUTS.CONNECT_START
			)
			if (result?.ok) {
				setConnected(prev => ({ ...prev, [key]: true }))
			} else {
				setErrors(prev => ({
					...prev,
					[key]: { message: result?.error || 'Connection failed' },
				}))
			}
		} catch (err) {
			setErrors(prev => ({ ...prev, [key]: messageForError(err) }))
		} finally {
			setConnecting(prev => (prev === key ? null : prev))
		}
	}

	const handleDisconnect = async key => {
		setErrors(prev => ({ ...prev, [key]: null }))
		try {
			if (!(await gateOnExtension(key))) return
			await callExtension(`${key}_DISCONNECT`, {}, TIMEOUTS.DISCONNECT)
			
			await api.delete(`/api/provider-sessions/${key}`)
		} catch (err) {
			setErrors(prev => ({ ...prev, [key]: messageForError(err) }))
		}
		setConnected(prev => ({ ...prev, [key]: false }))
	}

	const selectedKeys =
		!value || value === 'all'
			? []
			: value
					.split(',')
					.map(s => s.trim())
					.filter(Boolean)
	const allSelected = selectedKeys.length === 0 || selectedKeys.length === SOURCES.length
	const triggerLabel = allSelected
		? SOURCES.map(s => s.label).join(', ')
		: selectedKeys
				.map(k => SOURCES.find(s => s.key === k)?.label || k)
				.filter(Boolean)
				.join(', ')

	const toggleSource = key => {
		const baseKeys = allSelected ? SOURCES.map(s => s.key) : selectedKeys
		const set = new Set(baseKeys)
		if (set.has(key)) set.delete(key)
		else set.add(key)
		const next = Array.from(set)
		if (next.length === 0 || next.length === SOURCES.length) {
			onChange('all')
		} else {
			onChange(next.join(','))
		}
	}

	const selectAll = () => onChange('all')

	return (
		<div ref={wrapRef} className="w-full relative">
			<div className={tokens.slimFieldShell}>
				<Label>Source</Label>
				<button
					type="button"
					onClick={() => setIsOpen(v => !v)}
					className={`${inputBase} appearance-none cursor-pointer flex items-center justify-between text-left pr-5 h-[22px] leading-[22px]`}
				>
					<span className="truncate">{triggerLabel}</span>
				</button>
				<ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
			</div>

			{isOpen && (
				<div
					className={`absolute left-0 top-full mt-2 z-30 w-64 rounded-md py-1 ${tokens.dropdownBg}`}
				>
					<button
						type="button"
						onClick={selectAll}
						className={`w-full text-left px-3 py-2 text-[12px] ${
							allSelected ? `${activeItemCls} font-medium` : tokens.dropdownItem
						}`}
					>
						All sources
					</button>

					{SOURCES.map(({ key, label }) => {
						const isConn = !!connected[key]
						const isConnecting = connecting === key
						const isChecked = allSelected || selectedKeys.includes(key)
						const sourceError = errors[key]
						return (
							<div
								key={key}
								className={`pl-1.5 pr-3 py-1.5 ${
									isChecked && !allSelected ? activeItemCls : tokens.dropdownItem
								}`}
							>
								<div className="flex items-center justify-between">
									<button
										type="button"
										onClick={() => toggleSource(key)}
										className={`flex items-center gap-2 text-[12px] flex-1 text-left ${
											isChecked && !allSelected ? 'font-medium' : ''
										}`}
									>
										<input
											type="checkbox"
											checked={isChecked}
											onChange={() => toggleSource(key)}
											onClick={e => e.stopPropagation()}
											className="h-3.5 w-3.5 shrink-0 accent-sky-500 cursor-pointer"
											aria-label={`Select ${label}`}
										/>
										{SOURCE_LOGOS[key] ? (
											<img
												src={SOURCE_LOGOS[key]}
												alt=""
												className={`w-auto shrink-0 ${
													key === 'JBHUNT'
														? 'h-5 ml-1'
														: key === 'RXO'
															? 'h-2.5 ml-2'
															: key === 'LANDSTAR'
																? 'h-4'
																: 'h-5'
												}`}
												style={
													!isLight && key === 'TQL'
														? { filter: 'invert(1) hue-rotate(180deg)' }
														: !isLight && key === 'RXO'
															? { filter: 'invert(1)' }
															: undefined
												}
											/>
										) : (
											<span>{label}</span>
										)}
										{isConn ? (
											<CheckCircleIcon className="h-3.5 w-3.5 text-emerald-500" />
										) : (
											<ExclamationCircleIcon className="h-3.5 w-3.5 text-gray-400" />
										)}
									</button>
									{isConn ? (
										<button
											type="button"
											onClick={() => handleDisconnect(key)}
											className="text-[11px] text-gray-500 hover:text-rose-500"
										>
											Disconnect
										</button>
									) : isConnecting ? (
										<span className="inline-flex items-center gap-1 text-[11px] text-sky-500">
											<ArrowPathIcon className="h-3 w-3 animate-spin" />
											Connecting…
										</span>
									) : (
										<button
											type="button"
											onClick={() => handleConnect(key)}
											className="inline-flex items-center gap-1 text-[11px] text-sky-500 hover:text-sky-700"
										>
											<LinkIcon className="h-3 w-3" />
											Connect
										</button>
									)}
								</div>
								{sourceError && (
									<div className="mt-1 text-[10.5px] text-rose-500">
										{sourceError.message}
										{(sourceError.action === 'install' ||
											sourceError.action === 'update') && (
											<>
												{' '}
												<a
													href={STORE_URL}
													target="_blank"
													rel="noopener noreferrer"
													className="underline hover:no-underline"
												>
													{sourceError.action === 'install'
														? 'Install'
														: 'Update'}
												</a>
											</>
										)}
									</div>
								)}
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default SourceDropdown
